/**
 * Email operations
 * Handles all email-related operations using Resend API
 */

import { resendService } from '@/shared/services/email/resendService';
import type { InvoiceData, QuoteData } from '@/shared/types';

export const emailService = {
  /**
   * Send an email
   * @param to - Recipient email addresses
   * @param subject - Email subject
   * @param body - Email body content
   */
  async sendEmail(to: string[], subject: string, body: string): Promise<void> {
    try {
      if (!resendService.isConfigured()) {
        console.warn('Resend is not configured. Email would be sent:', { to, subject, bodyLength: body.length });
        return;
      }

      await resendService.sendEmail(subject, body, {
        to: to.map(email => ({ email })),
        subject,
        html: body,
        tags: ['manual_email']
      });

      console.log('Email sent successfully');
    } catch (error) {
      console.error('Failed to send email:', error);
      throw error;
    }
  },

  /**
   * Send invoice email
   * @param invoiceData - Invoice data for email template
   * @param toEmail - Recipient email address
   * @param clientName - Client name for personalization
   */
  async sendInvoice(invoiceData: InvoiceData, toEmail: string, clientName: string): Promise<void> {
    try {
      if (!resendService.isConfigured()) {
        console.warn('Resend is not configured. Invoice email would be sent to:', toEmail);
        // For demo purposes, still show what would be sent
        alert(`Demo: Invoice email would be sent to ${toEmail}\n\nInvoice #${invoiceData.invoiceNumber}\nAmount: ${invoiceData.currency} ${invoiceData.totalAmount.toFixed(2)}`);
        return;
      }

      await resendService.sendInvoice(
        { email: toEmail, name: clientName },
        invoiceData
      );

      console.log(`Invoice ${invoiceData.invoiceNumber} sent to ${toEmail}`);
    } catch (error) {
      console.error('Failed to send invoice email:', error);
      throw error;
    }
  },

  /**
   * Send quote email
   * @param quoteData - Quote data for email template
   * @param toEmail - Recipient email address
   * @param clientName - Client name for personalization
   */
  async sendQuote(quoteData: QuoteData, toEmail: string, clientName: string): Promise<void> {
    try {
      if (!resendService.isConfigured()) {
        console.warn('Resend is not configured. Quote email would be sent to:', toEmail);
        alert(`Demo: Quote email would be sent to ${toEmail}\n\nQuote #${quoteData.quoteNumber}\nAmount: ${quoteData.currency} ${quoteData.totalAmount.toFixed(2)}`);
        return;
      }

      await resendService.sendQuote(
        { email: toEmail, name: clientName },
        quoteData
      );

      console.log(`Quote ${quoteData.quoteNumber} sent to ${toEmail}`);
    } catch (error) {
      console.error('Failed to send quote email:', error);
      throw error;
    }
  }
};
